import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Camera, FolderLock, FileText, Receipt, Calendar, Download, Check, X, Loader2, LogOut, Mail, Eye, PenTool, Award } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Meridian Design System
const c = {
  forest: '#1A2E22', forest2: '#243D2E', forest3: '#2E4D3A', forest4: '#3A6148',
  cream: '#F5EDD8', cream2: '#EDE4CE', cream3: '#DDD4BC',
  copper: '#C47C3E', copper2: '#D4955A', copper3: '#E8B07A',
  muted: '#8A9E8E', muted2: '#6B7D6E', dark: '#0F1D14',
};

// Signature Pad
function SignaturePad({ onSave }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = c.copper;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (hasSignature) onSave(canvasRef.current.toDataURL());
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSave(null);
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed rounded-lg" style={{ borderColor: c.forest4, backgroundColor: c.forest }}>
        <canvas ref={canvasRef} width={400} height={150} className="w-full cursor-crosshair touch-none" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
      </div>
      <button onClick={clear} className="text-[11px] hover:underline" style={{ color: c.muted }}>Clear signature</button>
    </div>
  );
}

// Portal Layout
function PortalLayout({ children, client, onLogout }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: c.forest, color: c.cream }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet" />
      <header className="px-6 py-4" style={{ backgroundColor: c.dark, borderBottom: `1px solid ${c.forest2}` }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[9px] font-medium tracking-[2px] uppercase" style={{ color: c.copper }}>Meridian</span>
              <div className="flex-1 h-px w-8" style={{ backgroundColor: c.forest3 }}></div>
            </div>
            <h1 className="text-xl tracking-wide" style={{ fontFamily: "'Cormorant Garamond', serif", color: c.cream }}>RJ Brown</h1>
            <p className="text-[9px] tracking-[1.5px] uppercase" style={{ color: c.muted2 }}>Client Portal</p>
          </div>
          {client && (
            <div className="flex items-center gap-4">
              <span className="text-[12px]" style={{ color: c.muted }}>{client.name || client.email}</span>
              <button onClick={onLogout} style={{ color: c.muted }}><LogOut className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </header>
      <main className="max-w-4xl mx-auto p-6">{children}</main>
    </div>
  );
}

// Main Portal
export default function ClientPortal() {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('login');
  const [token, setToken] = useState(localStorage.getItem('portal_token'));
  const [client, setClient] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });

  const inputStyle = { backgroundColor: c.forest, border: `1px solid ${c.forest3}`, color: c.cream, fontFamily: "'IBM Plex Sans', sans-serif" };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/studio/portal/dashboard?token=${token}`);
      if (!res.ok) { localStorage.removeItem('portal_token'); setToken(null); setLoading(false); return; }
      const d = await res.json();
      setData(d);
      setClient(d.client);
    } catch (err) { console.error('Error fetching dashboard:', err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchDashboard(); else setLoading(false); }, [token, fetchDashboard]);

  const handleLogin = async () => {
    if (!form.email || !form.password) { toast.error('Email and password required'); return; }
    setAuthLoading(true);
    try {
      const res = await fetch(`${API}/api/studio/portal/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email, password: form.password }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      localStorage.setItem('portal_token', d.token);
      setToken(d.token);
      toast.success('Welcome back!');
    } catch (err) { toast.error(err.message); }
    setAuthLoading(false);
  };

  const handleRegister = async () => {
    if (!form.email || !form.password || !form.name) { toast.error('All fields required'); return; }
    setAuthLoading(true);
    try {
      const res = await fetch(`${API}/api/studio/portal/register`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Registered! Please login.');
      setAuthMode('login');
    } catch (err) { toast.error(err.message); }
    setAuthLoading(false);
  };

  const handleMagicLink = async () => {
    if (!form.email) { toast.error('Email required'); return; }
    setAuthLoading(true);
    try {
      const res = await fetch(`${API}/api/studio/portal/magic-link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.email }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      toast.success('Magic link sent!');
      if (d.link) toast.info(`Demo: ${d.link.split('/').pop()}`);
    } catch (err) { toast.error(err.message); }
    setAuthLoading(false);
  };

  const logout = () => { localStorage.removeItem('portal_token'); setToken(null); setClient(null); setData(null); };

  if (loading) return <PortalLayout client={null}><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: c.copper }} /></div></PortalLayout>;

  if (!token) {
    return (
      <PortalLayout client={null}>
        <div className="max-w-md mx-auto py-10">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="h-px w-10" style={{ backgroundColor: c.forest3 }}></div>
              <Award className="h-5 w-5" style={{ color: c.copper }} />
              <div className="h-px w-10" style={{ backgroundColor: c.forest3 }}></div>
            </div>
            <h2 className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: c.cream }}>
              Client <span style={{ color: c.copper }}>Portal</span>
            </h2>
          </div>

          <div className="flex gap-2 mb-6 justify-center">
            {['login', 'register', 'magic'].map((m) => (
              <button key={m} onClick={() => setAuthMode(m)} className="px-4 py-2.5 rounded-lg text-[13px] capitalize transition-all" style={{ backgroundColor: authMode === m ? c.copper : c.forest2, color: authMode === m ? c.dark : c.muted, border: `1px solid ${authMode === m ? c.copper : c.forest3}` }}>
                {m === 'magic' ? 'Magic Link' : m}
              </button>
            ))}
          </div>

          <div className="rounded-xl p-6 space-y-4" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
            {authMode === 'register' && <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full rounded-lg px-4 py-3 text-[13px] outline-none" style={inputStyle} />}
            <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email address" className="w-full rounded-lg px-4 py-3 text-[13px] outline-none" style={inputStyle} />
            {(authMode === 'login' || authMode === 'register') && <input type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Password" className="w-full rounded-lg px-4 py-3 text-[13px] outline-none" style={inputStyle} />}
            <button onClick={authMode === 'login' ? handleLogin : authMode === 'register' ? handleRegister : handleMagicLink} disabled={authLoading} className="w-full px-4 py-3 rounded-lg text-[13px] font-medium transition-all disabled:opacity-50" style={{ backgroundColor: c.copper, color: c.dark }}>
              {authLoading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : authMode === 'login' ? 'Login' : authMode === 'register' ? 'Create Account' : 'Send Magic Link'}
            </button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout client={client} onLogout={logout}>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[3px] mb-2" style={{ color: c.copper }}>Welcome back</p>
        <h2 className="text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif", color: c.cream }}>
          {client?.name || 'Client'}
        </h2>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Bookings', value: data?.bookings?.length || 0, icon: Calendar, color: c.copper },
          { label: 'Deliveries', value: data?.deliveries?.length || 0, icon: FolderLock, color: '#4ade80' },
          { label: 'Contracts', value: data?.contracts?.length || 0, icon: FileText, color: '#60a5fa' },
          { label: 'Invoices', value: data?.invoices?.length || 0, icon: Receipt, color: c.copper2 }
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={`item-${i}`} className="rounded-xl p-4" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
              <Icon className="h-5 w-5 mb-2" style={{ color: s.color }} />
              <div className="text-2xl font-light" style={{ color: s.color, fontFamily: "'Cormorant Garamond', serif" }}>{s.value}</div>
              <div className="text-[10px]" style={{ color: c.muted }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {/* Bookings */}
        <div className="rounded-xl p-5" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
          <h3 className="text-[14px] font-medium mb-4 flex items-center gap-2" style={{ color: c.cream }}><Calendar className="h-4 w-4" style={{ color: c.copper }} /> Upcoming Sessions</h3>
          {(data?.bookings || []).length === 0 ? <p className="text-[12px]" style={{ color: c.muted }}>No upcoming sessions</p> : (
            <div className="space-y-2">
              {data.bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: c.forest, border: `1px solid ${c.forest3}` }}>
                  <div>
                    <p className="text-[12px] font-medium" style={{ color: c.cream }}>{b.title}</p>
                    <p className="text-[10px]" style={{ color: c.muted }}>{b.date} {b.time && `at ${b.time}`}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: `${c.copper}15`, color: c.copper }}>{b.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Deliveries */}
        <div className="rounded-xl p-5" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
          <h3 className="text-[14px] font-medium mb-4 flex items-center gap-2" style={{ color: c.cream }}><FolderLock className="h-4 w-4" style={{ color: '#4ade80' }} /> Your Files</h3>
          {(data?.deliveries || []).length === 0 ? <p className="text-[12px]" style={{ color: c.muted }}>No files available</p> : (
            <div className="space-y-2">
              {data.deliveries.map((d) => (
                <Link key={d.id} to={`/portal/delivery/${d.id}?token=${token}`} className="flex items-center justify-between p-3 rounded-lg transition-colors" style={{ backgroundColor: c.forest, border: `1px solid ${c.forest3}` }}>
                  <div>
                    <p className="text-[12px] font-medium" style={{ color: c.cream }}>{d.title}</p>
                    <p className="text-[10px]" style={{ color: c.muted }}>{d.file_count} files</p>
                  </div>
                  <Download className="h-4 w-4" style={{ color: c.copper }} />
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Contracts */}
        <div className="rounded-xl p-5" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
          <h3 className="text-[14px] font-medium mb-4 flex items-center gap-2" style={{ color: c.cream }}><FileText className="h-4 w-4" style={{ color: '#60a5fa' }} /> Contracts</h3>
          {(data?.contracts || []).length === 0 ? <p className="text-[12px]" style={{ color: c.muted }}>No contracts</p> : (
            <div className="space-y-2">
              {data.contracts.map((ct) => (
                <Link key={ct.id} to={`/portal/contract/${ct.share_token}`} className="flex items-center justify-between p-3 rounded-lg transition-colors" style={{ backgroundColor: c.forest, border: `1px solid ${c.forest3}` }}>
                  <div>
                    <p className="text-[12px] font-medium" style={{ color: c.cream }}>{ct.title}</p>
                    <p className="text-[10px]" style={{ color: c.muted }}>{ct.status === 'signed' ? `Signed ${new Date(ct.signed_at).toLocaleDateString()}` : 'Awaiting signature'}</p>
                  </div>
                  {ct.status === 'signed' ? <Check className="h-4 w-4" style={{ color: '#4ade80' }} /> : <PenTool className="h-4 w-4" style={{ color: c.copper }} />}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Invoices */}
        <div className="rounded-xl p-5" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
          <h3 className="text-[14px] font-medium mb-4 flex items-center gap-2" style={{ color: c.cream }}><Receipt className="h-4 w-4" style={{ color: c.copper2 }} /> Invoices</h3>
          {(data?.invoices || []).length === 0 ? <p className="text-[12px]" style={{ color: c.muted }}>No invoices</p> : (
            <div className="space-y-2">
              {data.invoices.map((inv) => (
                <Link key={inv.id} to={`/portal/invoice/${inv.share_token}`} className="flex items-center justify-between p-3 rounded-lg transition-colors" style={{ backgroundColor: c.forest, border: `1px solid ${c.forest3}` }}>
                  <div>
                    <p className="text-[12px] font-medium" style={{ color: c.cream }}>{inv.title}</p>
                    <p className="text-[10px]" style={{ color: c.muted }}>{inv.invoice_number} · ${inv.total.toFixed(2)}</p>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: inv.status === 'paid' ? 'rgba(74,222,128,0.1)' : `${c.copper}15`, color: inv.status === 'paid' ? '#4ade80' : c.copper }}>
                    {inv.status === 'paid' ? 'Paid' : `Due: $${(inv.total - (inv.paid_amount || 0)).toFixed(2)}`}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}

// Contract Sign Page
export function ContractSign() {
  const { shareToken } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signerName, setSignerName] = useState('');

  const inputStyle = { backgroundColor: c.forest, border: `1px solid ${c.forest3}`, color: c.cream, fontFamily: "'IBM Plex Sans', sans-serif" };

  const fetchContract = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/studio/contracts/shared/${shareToken}`);
      if (!res.ok) throw new Error('Not found');
      setContract(await res.json());
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  }, [shareToken]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const signContract = async () => {
    if (!signerName) { toast.error('Please enter your name'); return; }
    if (contract.contract?.require_checkbox && !agreedToTerms) { toast.error('Please agree to terms'); return; }
    if (contract.contract?.require_signature && !signatureData) { toast.error('Please sign above'); return; }
    setSigning(true);
    try {
      const res = await fetch(`${API}/api/studio/contracts/shared/${shareToken}/sign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ signature_data: signatureData, agreed_to_terms: agreedToTerms, signer_name: signerName }) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Contract signed!');
      fetchContract();
    } catch (err) { toast.error(err.message); }
    setSigning(false);
  };

  if (loading) return <PortalLayout client={null}><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: c.copper }} /></div></PortalLayout>;
  if (!contract?.contract) return <PortalLayout client={null}><div className="text-center py-20"><p style={{ color: c.muted }}>Contract not found</p></div></PortalLayout>;

  const ct = contract.contract;

  return (
    <PortalLayout client={null}>
      <div className="max-w-2xl mx-auto">
        <h2 className="text-xl font-medium mb-4" style={{ color: c.copper }}>{ct.title}</h2>
        <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
          <div className="text-[12px] whitespace-pre-wrap leading-relaxed" style={{ color: c.cream2, fontFamily: "'IBM Plex Mono', monospace" }}>{ct.content}</div>
        </div>

        {contract.already_signed ? (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)' }}>
            <Check className="h-8 w-8 mx-auto mb-2" style={{ color: '#4ade80' }} />
            <p className="font-medium" style={{ color: '#4ade80' }}>Contract Signed</p>
            <p className="text-[12px] mt-1" style={{ color: 'rgba(74,222,128,0.7)' }}>by {ct.signature?.signer_name} on {new Date(ct.signed_at).toLocaleString()}</p>
          </div>
        ) : (
          <div className="rounded-xl p-6 space-y-4" style={{ backgroundColor: c.forest2, border: `1px solid ${c.copper}40` }}>
            {ct.terms?.length > 0 && (
              <div className="space-y-2">
                {ct.terms.map((term, i) => (
                  <label key={`item-${i}`} className="flex items-start gap-2 text-[12px]" style={{ color: c.cream2 }}>
                    <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} style={{ accentColor: c.copper, marginTop: 2 }} />
                    {term}
                  </label>
                ))}
              </div>
            )}
            <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Your full name *" className="w-full rounded-lg px-4 py-3 text-[13px] outline-none" style={inputStyle} />
            {ct.require_signature && (
              <div>
                <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: c.muted2 }}>Your Signature</p>
                <SignaturePad onSave={setSignatureData} />
              </div>
            )}
            <button onClick={signContract} disabled={signing} className="w-full px-4 py-3 rounded-lg text-[13px] font-medium transition-all disabled:opacity-50" style={{ backgroundColor: c.copper, color: c.dark }}>
              {signing ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Sign Contract'}
            </button>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

// Invoice Pay Page
export function InvoicePay() {
  const { shareToken } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/studio/invoices/shared/${shareToken}`);
      if (!res.ok) throw new Error('Not found');
      setInvoice(await res.json());
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  }, [shareToken]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const payWithStripe = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API}/api/studio/invoices/shared/${shareToken}/pay`, { method: 'POST' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      window.location.href = d.checkout_url;
    } catch (err) { toast.error(err.message); }
    setPaying(false);
  };

  if (loading) return <PortalLayout client={null}><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: c.copper }} /></div></PortalLayout>;
  if (!invoice) return <PortalLayout client={null}><div className="text-center py-20"><p style={{ color: c.muted }}>Invoice not found</p></div></PortalLayout>;

  const balance = invoice.total - (invoice.paid_amount || 0);

  return (
    <PortalLayout client={null}>
      <div className="max-w-2xl mx-auto">
        <h2 className="text-xl font-medium mb-2" style={{ color: c.copper }}>Invoice {invoice.invoice_number}</h2>
        <p className="text-[12px] mb-6" style={{ color: c.muted }}>{invoice.title}</p>

        <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: c.forest2, border: `1px solid ${c.forest3}` }}>
          <div className="space-y-2 mb-4">
            {invoice.items?.map((item, i) => (
              <div key={`item-${i}`} className="flex justify-between text-[13px]">
                <span style={{ color: c.cream2 }}>{item.description} × {item.quantity}</span>
                <span style={{ color: c.copper }}>${(item.quantity * item.unit_price).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="pt-3 space-y-1" style={{ borderTop: `1px solid ${c.forest3}` }}>
            <div className="flex justify-between text-[11px]" style={{ color: c.muted }}><span>Subtotal</span><span>${invoice.subtotal?.toFixed(2)}</span></div>
            <div className="flex justify-between text-[11px]" style={{ color: c.muted }}><span>Tax</span><span>${invoice.tax?.toFixed(2)}</span></div>
            <div className="flex justify-between text-[14px] font-medium" style={{ color: c.copper }}><span>Total</span><span>${invoice.total?.toFixed(2)}</span></div>
            {invoice.paid_amount > 0 && <div className="flex justify-between text-[11px]" style={{ color: '#4ade80' }}><span>Paid</span><span>-${invoice.paid_amount.toFixed(2)}</span></div>}
          </div>
        </div>

        {invoice.status === 'paid' ? (
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)' }}>
            <Check className="h-8 w-8 mx-auto mb-2" style={{ color: '#4ade80' }} />
            <p className="font-medium" style={{ color: '#4ade80' }}>Paid in Full</p>
          </div>
        ) : (
          <div className="rounded-xl p-6" style={{ backgroundColor: c.forest2, border: `1px solid ${c.copper}40` }}>
            <p className="text-lg font-medium mb-4" style={{ color: c.copper }}>Balance Due: ${balance.toFixed(2)}</p>
            <button onClick={payWithStripe} disabled={paying} className="w-full px-4 py-3 rounded-lg text-[13px] font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{ backgroundColor: c.copper, color: c.dark }}>
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Receipt className="h-4 w-4" /> Pay Now with Card</>}
            </button>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

// Magic Link Auth
export function MagicLinkAuth() {
  const { magicToken } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const verifyMagicLink = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/studio/portal/auth/${magicToken}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail);
      localStorage.setItem('portal_token', d.token);
      toast.success('Welcome!');
      navigate('/portal');
    } catch (err) { toast.error(err.message); navigate('/portal'); }
    setLoading(false);
  }, [magicToken, navigate]);

  useEffect(() => { verifyMagicLink(); }, [verifyMagicLink]);

  return <PortalLayout client={null}><div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: c.copper }} /><span className="ml-3" style={{ color: c.muted }}>Verifying...</span></div></PortalLayout>;
}
